import { useState } from 'react'
import { Modal } from '../ui/Modal'
import { LoginForm } from './LoginForm'
import { RegisterForm } from './RegisterForm'
import { ForgotPasswordForm } from './ForgotPasswordForm'
import { useAuth } from '../../contexts/AuthContext'

type AuthView = 'login' | 'register' | 'forgot-password'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  initialView?: AuthView
  title?: string
  description?: string
}

export function AuthModal({
  isOpen,
  onClose,
  initialView = 'login',
  title,
  description,
}: AuthModalProps) {
  const [view, setView] = useState<AuthView>(initialView)
  const { signIn, signUp, signInWithGoogle, resetPassword, error, loading, clearError } =
    useAuth()

  const handleClose = () => {
    clearError()
    setView(initialView)
    onClose()
  }

  const handleSignIn = async (email: string, password: string) => {
    await signIn(email, password)
    handleClose()
  }

  const handleSignUp = async (
    email: string,
    password: string,
    displayName: string
  ) => {
    await signUp(email, password, displayName)
    handleClose()
  }

  const handleGoogleSignIn = async () => {
    await signInWithGoogle()
    handleClose()
  }

  const handleResetPassword = async (email: string) => {
    await resetPassword(email)
  }

  const handleSwitchView = (newView: AuthView) => {
    clearError()
    setView(newView)
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm">
      {/* Custom header for special cases like rate limiting */}
      {(title || description) && (
        <div className="mb-6 text-center">
          {title && (
            <h2 className="text-xl font-bold text-gray-900 mb-2">{title}</h2>
          )}
          {description && (
            <p className="text-sm text-gray-600">{description}</p>
          )}
        </div>
      )}
      {view === 'login' && (
        <LoginForm
          onSubmit={handleSignIn}
          onGoogleSignIn={handleGoogleSignIn}
          onForgotPassword={() => handleSwitchView('forgot-password')}
          onSwitchToRegister={() => handleSwitchView('register')}
          error={error}
          loading={loading}
          hideHeader={!!(title || description)}
        />
      )}
      {view === 'register' && (
        <RegisterForm
          onSubmit={handleSignUp}
          onGoogleSignIn={handleGoogleSignIn}
          onSwitchToLogin={() => handleSwitchView('login')}
          error={error}
          loading={loading}
          hideHeader={!!(title || description)}
        />
      )}
      {view === 'forgot-password' && (
        <ForgotPasswordForm
          onSubmit={handleResetPassword}
          onBackToLogin={() => handleSwitchView('login')}
          error={error}
          loading={loading}
        />
      )}
    </Modal>
  )
}
